import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Send, Image as ImageIcon, X, Trash2, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import HubImage from "@/components/site/HubImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 15000;

const CATEGORIES = [
    { value: "encouragement", label: "Encouragement" },
    { value: "checkin", label: "Check-in" },
    { value: "prayer_request", label: "Prayer Request" },
    { value: "shared_wisdom", label: "Shared Wisdom" },
];

const EMOJIS = [
    { key: "heart", char: "\u2764\uFE0F" },
    { key: "pray", char: "\uD83D\uDE4F" },
    { key: "amen", char: "\u2728" },
    { key: "fire", char: "\uD83D\uDD25" },
    { key: "tear", char: "\uD83D\uDCA7" },
    { key: "dove", char: "\uD83D\uDD4A\uFE0F" },
];

function timeAgo(iso) {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function LoginGate() {
    const { login } = useAuth();
    return (
        <section className="min-h-[calc(100vh-200px)] flex items-center justify-center py-24 bg-sanct-sand">
            <div className="max-w-[520px] w-full mx-auto px-6 text-center">
                <div className="bg-sanct-cream rounded-[28px] p-12 border border-sanct-clay/40">
                    <Sparkles size={28} className="text-sanct-rose mx-auto mb-5" />
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-3">The Community Hub</p>
                    <h1 className="font-serif text-3xl md:text-4xl text-sanct-cocoa leading-tight mb-5">
                        Sign in to enter the circle.
                    </h1>
                    <p className="font-sans text-sm text-sanct-charcoal/75 leading-[1.7] mb-8">
                        A private, members-only space for encouragement, check-ins, prayer requests, and shared wisdom. Please sign in with Google to continue.
                    </p>
                    <button
                        onClick={login}
                        data-testid="hub-login-btn"
                        className="inline-flex items-center gap-3 px-8 py-4 bg-sanct-cocoa text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-rose transition-colors duration-300"
                    >
                        Continue with Google
                    </button>
                </div>
            </div>
        </section>
    );
}

function ComingSoon() {
    return (
        <section className="min-h-[calc(100vh-200px)] flex items-center justify-center py-24 bg-sanct-sand">
            <div className="max-w-[560px] mx-auto px-6 text-center">
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">The Community Hub</p>
                <h1 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1] tracking-tight">
                    Coming <span className="italic text-sanct-rose">soon.</span>
                </h1>
                <p className="font-sans text-base text-sanct-charcoal/80 leading-[1.8] mt-8">
                    A private, members-only sanctuary for encouragement, check-ins, prayer requests, and shared wisdom — opening alongside the fellowship.
                </p>
                <Link
                    to="/wmwe/fellowship#waitlist"
                    className="inline-flex items-center gap-3 mt-10 px-8 py-4 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors"
                >
                    Join the Waitlist
                </Link>
            </div>
        </section>
    );
}

function Composer({ onPosted }) {
    const [body, setBody] = useState("");
    const [category, setCategory] = useState("encouragement");
    const [imageKey, setImageKey] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [posting, setPosting] = useState(false);
    const fileRef = useRef(null);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image must be under 5 MB.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/hub/upload`, fd, { withCredentials: true });
            setImageKey(data.key);
            toast.success("Image attached.");
        } catch (err) {
            toast.error("Upload failed.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        setPosting(true);
        try {
            await axios.post(`${API}/hub/posts`, { category, body, image_key: imageKey }, { withCredentials: true });
            setBody("");
            setImageKey(null);
            setCategory("encouragement");
            onPosted?.();
        } catch {
            toast.error("Post failed.");
        } finally {
            setPosting(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-sanct-cream rounded-[24px] p-6 md:p-8 border border-sanct-clay/40" data-testid="hub-composer">
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                placeholder="Share what's on your heart…"
                className="w-full font-serif italic text-lg text-sanct-cocoa bg-transparent outline-none placeholder:text-sanct-cocoa/40 resize-none leading-[1.6]"
                data-testid="hub-composer-body"
            />

            {imageKey && (
                <div className="relative mt-3 w-fit">
                    <HubImage imageKey={imageKey} alt="attachment" className="max-h-40 rounded-xl" />
                    <button type="button" onClick={() => setImageKey(null)} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-sanct-cocoa text-sanct-cream flex items-center justify-center" aria-label="Remove image">
                        <X size={14} />
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-5 pt-5 border-t border-sanct-clay/40">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.value}
                        type="button"
                        onClick={() => setCategory(c.value)}
                        data-testid={`hub-cat-${c.value}`}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] rounded-full transition-colors ${
                            category === c.value
                                ? "bg-sanct-rose text-sanct-cream"
                                : "bg-sanct-sand text-sanct-cocoa/70 border border-sanct-clay/50 hover:border-sanct-rose"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
                <div className="flex-1" />
                <label className={`cursor-pointer text-sanct-cocoa/70 hover:text-sanct-rose p-2 rounded-full ${uploading ? "opacity-50" : ""}`} data-testid="hub-image-btn">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
                    <ImageIcon size={20} />
                </label>
                <button
                    type="submit"
                    disabled={!body.trim() || posting || uploading}
                    data-testid="hub-submit-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-sanct-cocoa text-sanct-cream text-[11px] uppercase tracking-[0.2em] rounded-full hover:bg-sanct-rose transition-colors disabled:opacity-50"
                >
                    <Send size={14} /> {posting ? "Posting…" : "Share"}
                </button>
            </div>
        </form>
    );
}

function Replies({ postId }) {
    const { user } = useAuth();
    const [replies, setReplies] = useState([]);
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/hub/posts/${postId}/replies`, { withCredentials: true });
            setReplies(data);
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

    const send = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        try {
            await axios.post(`${API}/hub/posts/${postId}/replies`, { body }, { withCredentials: true });
            setBody("");
            load();
        } catch {
            toast.error("Reply failed.");
        }
    };

    return (
        <div className="mt-5 pl-5 border-l-2 border-sanct-rose/30 space-y-4">
            {loading && <p className="font-sans text-xs text-sanct-charcoal/60">Loading replies…</p>}
            {replies.map((r) => (
                <div key={r.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-sanct-clay/60 overflow-hidden flex-shrink-0">
                        {r.author_picture && <img src={r.author_picture} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="bg-sanct-sand rounded-2xl px-4 py-3 flex-1">
                        <p className="font-sans text-xs text-sanct-cocoa/80 font-medium">{r.author_name} <span className="text-sanct-charcoal/50 font-normal">· {timeAgo(r.created_at)}</span></p>
                        <p className="font-sans text-sm text-sanct-charcoal mt-1 whitespace-pre-wrap leading-[1.6]">{r.body}</p>
                    </div>
                </div>
            ))}
            {user && (
                <form onSubmit={send} className="flex gap-2 items-center mt-3">
                    <input
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Write a reply…"
                        className="flex-1 font-sans text-sm bg-sanct-sand border border-sanct-clay/40 rounded-full px-4 py-2 outline-none focus:border-sanct-rose text-sanct-cocoa placeholder:text-sanct-cocoa/40"
                        data-testid={`hub-reply-input-${postId}`}
                    />
                    <button type="submit" className="p-2 text-sanct-cocoa/70 hover:text-sanct-rose"><Send size={16} /></button>
                </form>
            )}
        </div>
    );
}

function Post({ post, onChanged }) {
    const { user } = useAuth();
    const [openReplies, setOpenReplies] = useState(false);
    const reactions = post.reactions || {};
    const canDelete = user && (post.user_id === user.user_id || user.role === "admin");

    const toggleReaction = async (emoji) => {
        try {
            await axios.post(`${API}/hub/posts/${post.id}/react`, { emoji }, { withCredentials: true });
            onChanged?.();
        } catch {
            /* ignore */
        }
    };

    const del = async () => {
        if (!window.confirm("Delete this post?")) return;
        try {
            await axios.delete(`${API}/hub/posts/${post.id}`, { withCredentials: true });
            toast.success("Post removed.");
            onChanged?.();
        } catch {
            toast.error("Delete failed.");
        }
    };

    const catLabel = CATEGORIES.find((c) => c.value === post.category)?.label;

    return (
        <article className="bg-sanct-cream rounded-[24px] p-6 md:p-8 border border-sanct-clay/40" data-testid={`hub-post-${post.id}`}>
            <header className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-sanct-clay/60 overflow-hidden flex-shrink-0">
                    {post.author_picture && <img src={post.author_picture} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                    <p className="font-sans text-sm font-medium text-sanct-cocoa">{post.author_name}</p>
                    <p className="font-sans text-xs text-sanct-charcoal/60">{timeAgo(post.created_at)}</p>
                </div>
                <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-sanct-rose bg-sanct-rose/10 px-3 py-1 rounded-full">{catLabel}</span>
                {canDelete && (
                    <button onClick={del} className="text-sanct-cocoa/40 hover:text-red-500 p-1" data-testid={`hub-delete-${post.id}`}>
                        <Trash2 size={16} />
                    </button>
                )}
            </header>

            <p className="font-serif italic text-lg md:text-xl text-sanct-cocoa whitespace-pre-wrap leading-[1.6]">{post.body}</p>

            {post.image_key && (
                <div className="mt-5">
                    <HubImage imageKey={post.image_key} alt="attachment" className="rounded-xl max-h-96 w-full object-cover" />
                </div>
            )}

            <footer className="mt-6 pt-5 border-t border-sanct-clay/40 flex flex-wrap items-center gap-2">
                {EMOJIS.map((e) => {
                    const arr = reactions[e.key] || [];
                    const reacted = user && arr.includes(user.user_id);
                    return (
                        <button
                            key={e.key}
                            onClick={() => toggleReaction(e.key)}
                            data-testid={`hub-react-${post.id}-${e.key}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-base leading-none transition-colors ${
                                reacted
                                    ? "bg-sanct-rose text-sanct-cream"
                                    : "bg-sanct-sand text-sanct-cocoa hover:bg-sanct-clay/50"
                            }`}
                        >
                            <span style={{ fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif' }}>{e.char}</span>
                            {arr.length > 0 && <span className="text-xs">{arr.length}</span>}
                        </button>
                    );
                })}
                <div className="flex-1" />
                <button
                    onClick={() => setOpenReplies((v) => !v)}
                    data-testid={`hub-replies-toggle-${post.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-sanct-cocoa/70 hover:text-sanct-rose"
                >
                    <MessageCircle size={14} /> {post.reply_count || 0} {post.reply_count === 1 ? "reply" : "replies"}
                </button>
            </footer>

            {openReplies && <Replies postId={post.id} />}
        </article>
    );
}

export default function WMWEHub() {
    const { user, loading, logout } = useAuth();
    const [config, setConfig] = useState(null);
    const [posts, setPosts] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        axios.get(`${API}/hub/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
    }, []);

    const loadPosts = async () => {
        if (!user || !config?.enabled) return;
        try {
            const url = filter ? `${API}/hub/posts?category=${filter}` : `${API}/hub/posts`;
            const { data } = await axios.get(url, { withCredentials: true });
            setPosts(data);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        loadPosts();
        if (!user || !config?.enabled) return;
        const i = setInterval(loadPosts, POLL_MS);
        return () => clearInterval(i);
        // eslint-disable-next-line
    }, [user, filter, config]);

    if (loading || !config) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center bg-sanct-sand">
                <p className="font-serif italic text-xl text-sanct-cocoa">Loading…</p>
            </div>
        );
    }
    if (!config.enabled) return <ComingSoon />;
    if (!user) return <LoginGate />;

    return (
        <main data-testid="wmwe-hub" className="bg-sanct-sand text-sanct-charcoal min-h-[80vh]">
            {/* Hub Header */}
            <section className="bg-sanct-cream border-b border-sanct-clay/40 py-10 md:py-14">
                <div className="max-w-[900px] mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose">The Community Hub</p>
                            {user.role === "admin" && (
                                <span className="bg-sanct-cocoa text-sanct-cream text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full" data-testid="hub-admin-badge">Admin</span>
                            )}
                        </div>
                        <h1 className="font-serif text-3xl md:text-5xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Welcome, <span className="italic text-sanct-rose">{user.name?.split(" ")[0] || "beloved"}.</span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />}
                        <button onClick={logout} data-testid="hub-logout-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-sanct-cocoa/30 text-sanct-cocoa text-[11px] uppercase tracking-[0.2em] rounded-full hover:bg-sanct-cocoa hover:text-sanct-cream transition-colors">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="py-10 md:py-14">
                <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-6">
                    <Composer onPosted={loadPosts} />

                    <div className="flex flex-wrap items-center gap-2 pt-4">
                        <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-sanct-charcoal/60 mr-2">Filter</span>
                        <button
                            onClick={() => setFilter("")}
                            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] rounded-full transition-colors ${
                                filter === "" ? "bg-sanct-cocoa text-sanct-cream" : "bg-sanct-cream border border-sanct-clay/50 text-sanct-cocoa hover:border-sanct-rose"
                            }`}
                        >
                            All
                        </button>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.value}
                                onClick={() => setFilter(c.value)}
                                data-testid={`hub-filter-${c.value}`}
                                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] rounded-full transition-colors ${
                                    filter === c.value ? "bg-sanct-cocoa text-sanct-cream" : "bg-sanct-cream border border-sanct-clay/50 text-sanct-cocoa hover:border-sanct-rose"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {posts.length === 0 && (
                        <div className="text-center py-16">
                            <Heart size={28} className="text-sanct-rose mx-auto mb-5" />
                            <p className="font-serif italic text-2xl text-sanct-cocoa">Be the first to share.</p>
                            <p className="font-sans text-sm text-sanct-charcoal/70 mt-2">This sacred space begins with you.</p>
                        </div>
                    )}

                    {posts.map((p) => (
                        <Post key={p.id} post={p} onChanged={loadPosts} />
                    ))}
                </div>
            </section>
        </main>
    );
}
